import React from 'react';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useTheme } from 'styled-components';
import {
  AttachmentBrowseLink,
  AttachmentContainer,
  AttachmentFileLimit,
  AttachmentHelpArticlePhrase,
  AttachmentIconWrapper,
  AttachmentInfoFooter,
  AttachmentInfoFooterText,
  AttachmentText,
  AttachmentUploadArea,
  CircularProgress,
  DeleteIconWrapper,
  FileCard,
  FileCardsList,
  FileError,
  FileInfo,
  FileName,
} from '../FormPreview.styles';
import {
  handleDragEvent,
  MAX_PREVIEW_FILE_SIZE_MB,
  SUPPORTED_FILE_TYPES,
} from '../helpers';
import { AttachmentInputProps } from './types';

export const AttachmentInput: React.FC<AttachmentInputProps> = ({
  currentQuestion,
  answers,
  handleFileSelect,
  handleDeleteFile,
  handleFileDrop,
  fileInputId,
}) => {
  const theme = useTheme();
  const filesForQuestion = answers[currentQuestion.id]?.uploadedFiles || [];

  return (
    <AttachmentContainer data-testid="form-preview-attachment-container">
      <AttachmentUploadArea
        data-testid="form-preview-attachment-area"
        $hasFiles={filesForQuestion.length > 0}
        onDragOver={handleDragEvent}
        onDragEnter={handleDragEvent}
        onDragLeave={handleDragEvent}
        onDrop={handleFileDrop}
      >
        <input
          type="file"
          multiple
          accept={SUPPORTED_FILE_TYPES}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id={fileInputId}
        />
        <AttachmentIconWrapper>
          <StyledIcon
            className="uil uil-cloud-upload"
            size="24px"
            color={
              theme.isVaultVariant
                ? theme.newPalette.neutral.N800
                : theme.atlas.semantic.color.type.default
            }
          />
        </AttachmentIconWrapper>
        <AttachmentText>
          <strong>Drag files here or</strong>
          <br />
          <AttachmentBrowseLink
            as="label"
            htmlFor={fileInputId}
            data-testid="form-preview-attachment-browse"
          >
            select files to upload
          </AttachmentBrowseLink>
        </AttachmentText>
        <AttachmentFileLimit data-testid="form-preview-file-limit">
          Max. file size: {MAX_PREVIEW_FILE_SIZE_MB} MB
        </AttachmentFileLimit>
      </AttachmentUploadArea>
      <AttachmentInfoFooter data-testid="form-preview-attachment-info-footer">
        <AttachmentInfoFooterText>
          For more information on supported file types read this{' '}
          <AttachmentHelpArticlePhrase data-testid="form-preview-attachment-help-article-phrase">
            help article
          </AttachmentHelpArticlePhrase>
          .
        </AttachmentInfoFooterText>
      </AttachmentInfoFooter>
      {filesForQuestion.length > 0 && (
        <FileCardsList>
          {filesForQuestion.map((file) => {
            const progress = file.progress ?? 100;
            const isUploading = progress < 100;
            const hasError = file.error;

            let statusIconElement;

            if (hasError) {
              statusIconElement = (
                <StyledIcon
                  className="uil uil-exclamation-circle"
                  size="24px"
                  color={theme.newPalette.red.R500}
                />
              );
            } else if (isUploading) {
              statusIconElement = (
                <CircularProgress $progress={progress} viewBox="0 0 24 24">
                  <circle className="inner" cx="12" cy="12" r="4" />
                  <circle className="bg" cx="12" cy="12" r="10" />
                  <circle className="progress" cx="12" cy="12" r="10" />
                </CircularProgress>
              );
            } else {
              statusIconElement = (
                <StyledIcon
                  className="uil uil-check-circle"
                  size="24px"
                  color={theme.newPalette.green.G500}
                />
              );
            }

            return (
              <FileCard key={file.id} data-testid={`file-card-${file.id}`}>
                {statusIconElement}
                <FileInfo>
                  <FileName data-testid={`file-name-${file.id}`}>
                    {file.name}
                  </FileName>
                  {hasError ? (
                    <FileError data-testid={`file-error-${file.id}`}>
                      {file.error}
                    </FileError>
                  ) : null}
                </FileInfo>
                <DeleteIconWrapper
                  onClick={() => handleDeleteFile(file.id)}
                  data-testid={`delete-file-${file.id}`}
                  aria-label={`Remove file ${file.name}`}
                >
                  <StyledIcon
                    className={
                      isUploading ? 'uil uil-minus-circle' : 'uil uil-trash-alt'
                    }
                    size="20px"
                    color={
                      theme.isVaultVariant
                        ? theme.newPalette.neutral.N800
                        : theme.atlas.semantic.color.type.default
                    }
                  />
                </DeleteIconWrapper>
              </FileCard>
            );
          })}
        </FileCardsList>
      )}
    </AttachmentContainer>
  );
};
